/**
 * User-facing copy for push notification states.
 *
 * Two states cannot be resolved by clicking anything in our UI, so each one
 * gets real instructions instead of a button that would silently do nothing:
 *
 *   ios-needs-install — iOS only exposes web push to a site that has been added
 *                       to the Home Screen and opened from that icon.
 *   denied            — a permission the user has blocked cannot be re-requested
 *                       programmatically. It must be changed in browser settings.
 */

export const IOS_INSTALL_STEPS = [
  'Tap the Share button at the bottom of Safari (the square with an arrow).',
  'Scroll down and tap "Add to Home Screen".',
  'Tap "Add", then open USABG from the new icon on your Home Screen.',
  'Come back to this page and tap "Turn On Notifications".',
]

/**
 * The steps above name Safari's Share button specifically, and every iOS
 * browser is WebKit underneath but reaches "Add to Home Screen" differently.
 * Sending a Chrome-on-iPhone user through Safari's flow is one instruction, not
 * a second set of steps to keep in sync.
 */
export const IOS_USE_SAFARI_NOTE =
  'On iPhone and iPad these steps only work in Safari. Open USABG in Safari first, then follow them there.'

/** Where the install steps live permanently, for anyone who dismissed the card. */
export const IOS_STEPS_AGAIN_NOTE =
  'You can find these steps again at any time under Alerts → Settings.'

const UNBLOCK_STEPS = {
  Chrome: [
    'Click the icon to the left of the web address (a tune or lock icon).',
    'Find "Notifications" and switch it to Allow.',
    'Reload this page.',
  ],
  Edge: [
    'Click the icon to the left of the web address (a lock icon).',
    'Find "Notifications" and set it to Allow.',
    'Reload this page.',
  ],
  Firefox: [
    'Click the lock icon to the left of the web address.',
    'Next to "Send Notifications", click the X to clear the blocked setting.',
    'Reload this page and choose Allow when asked.',
  ],
  Safari: [
    'Open Safari > Settings (or Preferences) > Websites > Notifications.',
    'Find usabg.com in the list and set it to Allow.',
    'Reload this page.',
  ],
  Opera: [
    'Click the lock icon to the left of the web address.',
    'Open Site settings and set Notifications to Allow.',
    'Reload this page.',
  ],
}

const GENERIC_UNBLOCK_STEPS = [
  'Open your browser settings for this site.',
  'Set Notifications to Allow.',
  'Reload this page.',
]

// Phones do not have the desktop chrome the steps above describe — no menu bar,
// nothing to "click". Keyed by platform rather than browser because the phone's
// own settings app is the reliable route on both.
const MOBILE_UNBLOCK_STEPS = {
  android: [
    'Tap the icon to the left of the web address (a tune or lock icon).',
    'Tap "Permissions", find "Notifications" and switch it to Allow.',
    'Reload this page.',
  ],
  ios: [
    'Open the iPhone Settings app → Notifications → USABG.',
    'Turn "Allow Notifications" on.',
    'Reopen USABG from your Home Screen icon.',
  ],
}

export const getUnblockSteps = (browser, platform) =>
  MOBILE_UNBLOCK_STEPS[platform] ||
  UNBLOCK_STEPS[browser] ||
  GENERIC_UNBLOCK_STEPS

/**
 * OS-level suppression instructions.
 *
 * This is the one failure the web cannot detect: if the operating system is
 * blocking the browser, `Notification.permission` still reports "granted" and
 * showNotification() resolves without error — nothing appears and nothing
 * throws. The only way to find out is to show a notification and ask the
 * person whether they saw it.
 */
const OS_STEPS = {
  macos: [
    'Open the Apple menu → System Settings → Notifications.',
    'Find your browser in the list (e.g. Google Chrome) and click it.',
    'Turn "Allow Notifications" on.',
    'Come back here and run the test again.',
  ],
  windows: [
    'Open Start → Settings → System → Notifications.',
    'Make sure notifications are on, and that Focus Assist / Do Not Disturb is off.',
    'Find your browser in the app list and turn its notifications on.',
    'Come back here and run the test again.',
  ],
  android: [
    'Open your phone Settings → Apps → your browser → Notifications.',
    'Turn notifications on.',
    'Come back here and run the test again.',
  ],
  ios: [
    'Open Settings → Notifications → USABG.',
    'Turn "Allow Notifications" on.',
    'Come back here and run the test again.',
  ],
}

const OS_STEPS_GENERIC = [
  'Open your device notification settings.',
  'Allow notifications for your browser, and turn off Do Not Disturb / Focus.',
  'Come back here and run the test again.',
]

export const getOsUnblockSteps = (platform) =>
  OS_STEPS[platform] || OS_STEPS_GENERIC

/** Short, honest sentence describing a device row's delivery status. */
export const describeDeviceStatus = (device) => {
  if (!device) return ''
  // Suppressed is checked first: it is the deliberate "stay off" state, and it
  // outranks a plain revoke (which is just a sign-out release).
  if (device.suppressed_at) return 'Turned off'
  if (device.revoked_at) return 'Signed out'
  switch (device.permission_state) {
    case 'granted':
      // iOS grants permission only inside the installed app, so a granted row
      // that is not standalone cannot actually receive anything.
      if (`${device.platform}` === 'ios' && !parseInt(device.is_standalone))
        return 'Not added to Home Screen'
      // The agent ran the self-test and told us nothing appeared. Believe them
      // over the browser: OS-level suppression leaves permission reading
      // "granted" and showNotification() resolving, so this is the only signal
      // there is. A later click proves delivery and supersedes it.
      if (device.verify_failed_at && !device.last_clicked_at)
        return 'Not appearing on this device'
      return 'Receiving notifications'
    case 'denied':
      return 'Blocked in browser settings'
    case 'unsupported':
      return 'Not supported by this browser'
    default:
      return 'Not turned on'
  }
}
